import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ShieldCheck, Droplets, Wind, Warehouse, FlaskConical, SprayCan, Tractor,
  Wrench, Cog, MoreHorizontal, ArrowLeft, Check, CheckCircle2, Loader2, Clock,
} from "lucide-react";
import { submitLead } from "@/lib/leadSubmit";
import { validateEmail } from "@/lib/validateEmail";
import { toast } from "@/hooks/use-toast";

type StepId = "interest" | "fleet" | "timeline" | "company" | "contact";

const INTERESTS = [
  { label: "Protect My Fleet", icon: ShieldCheck, blurb: "Salt & corrosion protection" },
  { label: "Pressure Washers", icon: Droplets, blurb: "Hot & cold water systems" },
  { label: "Air Compressors", icon: Wind, blurb: "Shop & industrial air" },
  { label: "Wash Bay", icon: Warehouse, blurb: "Design & installation" },
  { label: "Chemicals", icon: FlaskConical, blurb: "Cleaning & disinfecting" },
  { label: "Detergents", icon: SprayCan, blurb: "Degreasers & soaps" },
  { label: "City Master", icon: Tractor, blurb: "Municipal sweepers" },
  { label: "Repair Request", icon: Wrench, blurb: "Service & maintenance" },
  { label: "Pressure Washer Parts", icon: Cog, blurb: "Wands, hoses & more" },
  { label: "Other", icon: MoreHorizontal, blurb: "Tell us what you need" },
] as const;

const FLEET_SIZES = ["1 – 9", "10 – 24", "25 – 50"] as const;
const FLEET_LARGE = "50 +";

const TIMELINES = [
  "As soon as possible",
  "Within 1 – 3 months",
  "Within 3 – 6 months",
  "Just researching",
] as const;

/** Inputs stay at 16px on mobile — anything smaller makes iOS zoom the viewport. */
const FIELD =
  "w-full rounded-xl border border-input bg-white/70 px-4 py-3 text-base outline-none backdrop-blur transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/30 md:text-sm";

/**
 * Multi-step "Free Consultation" request.
 *
 * One question per screen so the visitor is never staring at a wall of fields:
 * tap-only steps come first (they build momentum and can auto-advance), and the
 * typing-heavy steps come last, once the visitor is already invested.
 *
 * Everything the visitor answers is folded into `message` on submit, because
 * the team's notification email (buildLeadEmailHtml) only renders a fixed set
 * of columns and ignores `extra` — `extra` is still sent so the structured
 * answers land in the leads table for reporting.
 */
export interface ConsultationFormProps {
  /**
   * Pre-selects the interest and drops the "what can we help with" step, so the
   * form opens on the next question. Used by the Fleet Preservation assessment
   * CTA, which already knows why the visitor clicked.
   */
  presetInterest?: string;
  /** Lead `form_name`, so preset entry points are reportable separately. */
  formName?: string;
  /** Overrides the final submit button label. */
  submitLabel?: string;
  /** Replaces the copy shown on the success panel. */
  successNote?: string;
}

export default function ConsultationForm({
  presetInterest,
  formName = "Free Consultation",
  submitLabel = "Request my free consultation",
  successNote,
}: ConsultationFormProps = {}) {
  const [interest, setInterest] = useState(presetInterest ?? "");
  const [otherText, setOtherText] = useState("");
  const [fleetSize, setFleetSize] = useState("");
  const [timeline, setTimeline] = useState("");
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  const [emailError, setEmailError] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // The fleet-size question only exists when they're here about their fleet.
  // A preset interest means that question was already answered by the button
  // they clicked, so the step is dropped rather than shown pre-filled.
  const steps = useMemo<StepId[]>(
    () => [
      ...(presetInterest ? [] : (["interest"] as StepId[])),
      ...(interest === "Protect My Fleet" ? (["fleet"] as StepId[]) : []),
      "timeline",
      "company",
      "contact",
    ],
    [interest, presetInterest],
  );
  const step = steps[Math.min(stepIndex, steps.length - 1)];

  const go = (delta: number) => {
    setDirection(delta);
    setStepIndex((i) => Math.max(0, Math.min(steps.length - 1, i + delta)));
  };

  const pickInterest = (label: string) => {
    setInterest(label);
    if (label !== "Protect My Fleet") setFleetSize("");
    // "Other" needs them to type first, so hold position; everything else moves on.
    if (label !== "Other") {
      setOtherText("");
      setTimeout(() => go(1), 180);
    }
  };

  const pickAndAdvance = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setTimeout(() => go(1), 180);
  };

  const checkEmail = (value: string) => {
    if (!value.trim()) {
      setEmailError("");
      return false;
    }
    const c = validateEmail(value);
    setEmailError(c.valid ? "" : c.message);
    return c.valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({ title: "Contact name required", description: "Please tell us who to ask for.", variant: "destructive" });
      return;
    }
    if (!phone.trim()) {
      toast({ title: "Phone required", description: "Please enter a phone number we can reach you at.", variant: "destructive" });
      return;
    }
    if (!email.trim() || !checkEmail(email)) {
      toast({ title: "Email required", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }

    const interestLabel = interest === "Other" && otherText.trim() ? `Other — ${otherText.trim()}` : interest;
    const summary = [
      `Interest: ${interestLabel}`,
      fleetSize ? `Fleet size: ${fleetSize} vehicles` : null,
      timeline ? `Timeline: ${timeline}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    setSubmitting(true);
    const res = await submitLead({
      form_name: formName,
      name: name.trim(),
      company: company.trim() || undefined,
      email: email.trim(),
      phone: phone.trim(),
      request_type: interestLabel || "Free Consultation",
      category_context: interest || undefined,
      product_context: fleetSize ? `Fleet size: ${fleetSize}` : undefined,
      message: notes.trim() ? `${summary}\n\nAdditional notes:\n${notes.trim()}` : summary,
      extra: {
        interest: interest || undefined,
        interest_other: otherText.trim() || undefined,
        fleet_size: fleetSize || undefined,
        timeline: timeline || undefined,
        notes: notes.trim() || undefined,
      },
    });
    setSubmitting(false);

    if (!res.ok) {
      toast({ title: "Could not send your request", description: res.error, variant: "destructive" });
      return;
    }
    setSuccess(true);
  };

  const progress = success ? 100 : ((stepIndex + 1) / steps.length) * 100;

  return (
    <div className="consult-aurora mx-auto w-full max-w-3xl">
      <div className="glass-strong overflow-hidden rounded-3xl">
        {/* Progress rail */}
        <div className="h-1.5 w-full bg-white/40">
          <motion.div
            className="h-full rounded-r-full bg-gradient-to-r from-primary to-accent"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ type: "spring", stiffness: 180, damping: 26 }}
          />
        </div>

        <div className="p-6 sm:p-10">
          {success ? (
            <div className="py-10 text-center">
              <CheckCircle2 className="mx-auto h-16 w-16 text-accent" />
              <h3 className="mt-5 font-heading text-2xl font-bold">Request received</h3>
              <p className="mx-auto mt-3 max-w-md text-muted-foreground">
                Thanks{name.trim() ? `, ${name.trim().split(" ")[0]}` : ""} —{" "}
                {successNote ??
                  "a specialist will reach out within 1–2 business days to schedule your free on-site consultation."}
              </p>
            </div>
          ) : (
            <>
              <div className="mb-6 flex items-center justify-between gap-4">
                <span className="text-xs font-semibold uppercase tracking-widest text-primary">
                  Step {stepIndex + 1} of {steps.length}
                </span>
                {stepIndex > 0 && (
                  <button
                    type="button"
                    onClick={() => go(-1)}
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                )}
              </div>

              <AnimatePresence mode="wait" initial={false} custom={direction}>
                <motion.div
                  key={step}
                  custom={direction}
                  initial={{ opacity: 0, x: direction * 28 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction * -28 }}
                  transition={{ duration: 0.28, ease: "easeOut" }}
                >
                  {step === "interest" && (
                    <StepShell
                      title="What can we help you with?"
                      hint="Pick the closest match — we'll tailor the visit around it."
                    >
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {INTERESTS.map(({ label, icon: Icon, blurb }) => (
                          <OptionCard
                            key={label}
                            selected={interest === label}
                            onClick={() => pickInterest(label)}
                          >
                            <Icon className="h-6 w-6 text-primary" />
                            <span className="mt-2 block text-sm font-semibold leading-tight">{label}</span>
                            <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">{blurb}</span>
                          </OptionCard>
                        ))}
                      </div>

                      {interest === "Other" && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="overflow-hidden"
                        >
                          <div className="pt-5">
                            <label htmlFor="consult-other" className="mb-2 block text-sm font-semibold">
                              What are you looking for?
                            </label>
                            <input
                              id="consult-other"
                              autoFocus
                              value={otherText}
                              onChange={(e) => setOtherText(e.target.value)}
                              placeholder="Tell us what you need…"
                              className={FIELD}
                            />
                            <PrimaryButton
                              className="mt-4"
                              disabled={!otherText.trim()}
                              onClick={() => go(1)}
                            >
                              Continue
                            </PrimaryButton>
                          </div>
                        </motion.div>
                      )}
                    </StepShell>
                  )}

                  {step === "fleet" && (
                    <StepShell
                      title="How many vehicles are in your fleet?"
                      hint="This helps us size the right protection program."
                    >
                      <div className="grid grid-cols-3 gap-3">
                        {FLEET_SIZES.map((size) => (
                          <OptionCard
                            key={size}
                            selected={fleetSize === size}
                            onClick={() => pickAndAdvance(setFleetSize)(size)}
                            className="text-center"
                          >
                            <span className="block font-heading text-xl font-bold">{size}</span>
                            <span className="mt-0.5 block text-[11px] text-muted-foreground">vehicles</span>
                          </OptionCard>
                        ))}
                      </div>
                      <div className="mt-3">
                        <OptionCard
                          selected={fleetSize === FLEET_LARGE}
                          onClick={() => pickAndAdvance(setFleetSize)(FLEET_LARGE)}
                          className="text-center"
                        >
                          <span className="block font-heading text-xl font-bold">{FLEET_LARGE}</span>
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">
                            vehicles — large fleet program
                          </span>
                        </OptionCard>
                      </div>
                    </StepShell>
                  )}

                  {step === "timeline" && (
                    <StepShell
                      title="What's your timeline?"
                      hint="No wrong answer — it just tells us how quickly to move."
                    >
                      <div className="grid gap-3 sm:grid-cols-2">
                        {TIMELINES.map((t) => (
                          <OptionCard
                            key={t}
                            selected={timeline === t}
                            onClick={() => pickAndAdvance(setTimeline)(t)}
                          >
                            <span className="flex items-center gap-2.5">
                              <Clock className="h-5 w-5 shrink-0 text-primary" />
                              <span className="text-sm font-semibold">{t}</span>
                            </span>
                          </OptionCard>
                        ))}
                      </div>
                    </StepShell>
                  )}

                  {step === "company" && (
                    <StepShell title="What's your company name?" hint="So we know who we're visiting.">
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          go(1);
                        }}
                      >
                        <input
                          autoFocus
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          placeholder="Company name"
                          className={FIELD}
                        />
                        <div className="mt-4 flex items-center gap-3">
                          <PrimaryButton type="submit" disabled={!company.trim()}>
                            Continue
                          </PrimaryButton>
                          <button
                            type="button"
                            onClick={() => go(1)}
                            className="text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
                          >
                            Skip
                          </button>
                        </div>
                      </form>
                    </StepShell>
                  )}

                  {step === "contact" && (
                    <StepShell title="How should we reach you?" hint="We'll only use this to schedule your visit.">
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                          <label htmlFor="consult-name" className="mb-1.5 block text-sm font-semibold">
                            Contact name <span className="text-destructive">*</span>
                          </label>
                          <input
                            id="consult-name"
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className={FIELD}
                          />
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label htmlFor="consult-phone" className="mb-1.5 block text-sm font-semibold">
                              Phone number <span className="text-destructive">*</span>
                            </label>
                            <input
                              id="consult-phone"
                              type="tel"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              required
                              className={FIELD}
                            />
                          </div>
                          <div>
                            <label htmlFor="consult-email" className="mb-1.5 block text-sm font-semibold">
                              Email address <span className="text-destructive">*</span>
                            </label>
                            <input
                              id="consult-email"
                              type="email"
                              value={email}
                              onChange={(e) => {
                                setEmail(e.target.value);
                                if (emailError) setEmailError("");
                              }}
                              onBlur={(e) => checkEmail(e.target.value)}
                              aria-invalid={!!emailError}
                              required
                              className={`${FIELD} ${emailError ? "border-destructive" : ""}`}
                            />
                            {emailError && <p className="mt-1 text-xs text-destructive">{emailError}</p>}
                          </div>
                        </div>
                        <div>
                          <label htmlFor="consult-notes" className="mb-1.5 block text-sm font-semibold">
                            Additional notes <span className="font-normal text-muted-foreground">(optional)</span>
                          </label>
                          <textarea
                            id="consult-notes"
                            rows={4}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Anything else that would help us prepare…"
                            className={FIELD}
                          />
                        </div>
                        <PrimaryButton type="submit" disabled={submitting} className="w-full sm:w-auto">
                          {submitting ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4" /> {submitLabel}
                            </>
                          )}
                        </PrimaryButton>
                      </form>
                    </StepShell>
                  )}
                </motion.div>
              </AnimatePresence>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StepShell({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-heading text-2xl font-bold sm:text-3xl">{title}</h3>
      {hint && <p className="mt-2 text-sm text-muted-foreground">{hint}</p>}
      <div className="mt-6">{children}</div>
    </div>
  );
}

function OptionCard({
  selected,
  onClick,
  className,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} data-selected={selected} className="consult-option text-left">
      <span className={`consult-option__face block p-4 ${className || ""}`}>
        {children}
        {selected && (
          <span className="absolute right-2.5 top-2.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <Check className="h-3 w-3" />
          </span>
        )}
      </span>
    </button>
  );
}

function PrimaryButton({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary to-secondary px-6 py-3 font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/35 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none ${className || ""}`}
    >
      {children}
    </button>
  );
}
